/**
 * The four complete indexes: every hymn, tune, person and metre.
 *
 * These carry the crawlable-completeness job the old 720-row home page was doing,
 * and they do it better: each is a fixed URL needing no query string, each is
 * linked from the index and from 32 section pages, and none of them is what a
 * human lands on when they arrive wanting to plan a service.
 *
 * `/people` matters most. 607 people have pages and only a handful were reachable
 * before — from a hymn that happened to credit them. Filing is by SURNAME (see
 * `sortNameFor`), because "Ralph Vaughan Williams" belongs under V and the corpus
 * has no sortable name field.
 *
 * One component with a `kind`, not four files: the four differ only in what a row
 * says, and splitting them would put the same header, the same head copy shape and
 * the same A–Z bucketing in four places to drift.
 */
import { Link } from 'react-router-dom'

import { HYMNAL_1982, pages } from '@/data/hymnal'
import { Crumb, SectionTitle } from '@/components/HymnList'
import { PageHead } from '@/components/PageHead'
// personSort*, NOT the raw sort-name helpers: a hymnal credit carries its life
// dates ("John Mason Neale, 1818-1866"), and filing on that string puts people
// under a digit. Calling the raw ones here while a unit test exercised the
// wrapper is exactly how 47 people ended up under `1`, `3`, `5` and `(`.
import { comparePersonByFileName, personSortLetter } from '@/lib/hymnal'

const BASE = `/hymnal/${HYMNAL_1982.id}`

/** A row in any of the indexes, already resolved to a destination. */
interface Row {
  key: string
  href: string
  label: string
  detail?: string
  /** The A–Z bucket. Absent on metres, which are ordered by frequency. */
  bucket?: string
}

function bucketize(rows: Row[]): Map<string, Row[]> {
  const out = new Map<string, Row[]>()
  for (const r of rows) {
    const b = r.bucket ?? '#'
    out.set(b, [...(out.get(b) ?? []), r])
  }
  return new Map([...out.entries()].sort(([a], [b]) => a.localeCompare(b)))
}

const firstLetter = (s: string): string => {
  const c = (s.trim()[0] ?? '#').toUpperCase()
  return /[A-Z]/.test(c) ? c : '#'
}

export type IndexKind = 'hymns' | 'tunes' | 'people' | 'meters'

const BUILD: Record<IndexKind, () => { title: string; blurb: string; rows: Row[]; bucketed: boolean }> = {
  hymns: () => ({
    title: 'All hymns A–Z',
    blurb: `Every one of the ${pages.hymns.length} hymns in ${HYMNAL_1982.label}, by first line.`,
    bucketed: true,
    rows: [...pages.hymns]
      .sort((a, b) => a.firstLine.localeCompare(b.firstLine))
      .map((h) => ({
        key: h.slug,
        href: `${BASE}/hymn/${h.slug}`,
        label: h.firstLine,
        detail: [h.number, h.tuneName].filter(Boolean).join(' · '),
        bucket: firstLetter(h.firstLine),
      })),
  }),
  tunes: () => ({
    title: 'Tunes',
    blurb: `The ${pages.tunes.length} tunes used by more than one hymn. A tune used by a single hymn is named on that hymn's page.`,
    bucketed: true,
    rows: [...pages.tunes]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        key: t.slug,
        href: `${BASE}/tune/${t.slug}`,
        label: t.name,
        detail: [t.meter, `${t.hymns.length} hymns`].filter(Boolean).join(' · '),
        bucket: firstLetter(t.name),
      })),
  }),
  people: () => ({
    title: 'People',
    blurb: `Everyone credited in ${HYMNAL_1982.label} — authors, translators, composers, arrangers and harmonizers — filed by surname.`,
    bucketed: true,
    rows: [...pages.people]
      .sort((a, b) => comparePersonByFileName(a.name, b.name))
      .map((p) => ({
        key: p.slug,
        href: `${BASE}/person/${p.slug}`,
        label: p.name,
        detail: `${p.hymns.length} ${p.hymns.length === 1 ? 'hymn' : 'hymns'}`,
        bucket: personSortLetter(p.name),
      })),
  }),
  meters: () => ({
    title: 'Metres',
    blurb:
      'Metres with ten or more hymns, commonest first. Any text in a metre can be sung to any tune in it — which is what makes a substitution possible.',
    bucketed: false,
    rows: pages.meters.map((m) => ({
      key: m.slug,
      href: `${BASE}/meter/${m.slug}`,
      label: m.meter,
      detail: `${m.hymns.length} hymns`,
    })),
  }),
}

export function IndexPage({ kind }: { kind: IndexKind }) {
  const { title, blurb, rows, bucketed } = BUILD[kind]()
  const buckets = bucketed ? bucketize(rows) : null

  const RowLink = ({ r }: { r: Row }) => (
    <li className="py-1 text-sm">
      <Link
        to={r.href}
        className="group flex min-h-11 touch-manipulation items-baseline justify-between gap-3 py-1 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <span className="text-muted-foreground group-hover:text-foreground">{r.label}</span>
        {r.detail && <span className="shrink-0 font-mono text-xs text-muted-foreground">{r.detail}</span>}
      </Link>
    </li>
  )

  return (
    <main className="mx-auto max-w-4xl px-5 py-12">
      <PageHead
        title={title}
        description={blurb}
        path={`${BASE}/${kind}`}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${title} — ${HYMNAL_1982.label}`,
          isPartOf: { '@type': 'WebSite', name: HYMNAL_1982.label },
          mainEntity: { '@type': 'ItemList', numberOfItems: rows.length },
        }}
      />
      <Crumb label={title} />

      <header className="mb-8">
        <h1 className="mb-3 font-display text-3xl text-foreground sm:text-4xl">{title}</h1>
        <p className="max-w-2xl text-muted-foreground">{blurb}</p>
      </header>

      {buckets ? (
        [...buckets.entries()].map(([letter, list]) => (
          <div key={letter} className="mb-8">
            <h2 className="mb-2 border-b border-border pb-1 font-display text-lg text-foreground/80">{letter}</h2>
            <ul className="grid gap-x-6 sm:grid-cols-2">
              {list.map((r) => (
                <RowLink key={r.key} r={r} />
              ))}
            </ul>
          </div>
        ))
      ) : (
        <>
          <SectionTitle>By frequency</SectionTitle>
          <ul className="grid gap-x-6 sm:grid-cols-2">
            {rows.map((r) => (
              <RowLink key={r.key} r={r} />
            ))}
          </ul>
        </>
      )}
    </main>
  )
}
